import { Button, Group, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useLocation, useMatchRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { sendersPageContactOptionsQueries } from "routes/_auth/organization/senders/$senderId";
import { GroupSearchCombobox } from "shared/components/organization/users/GroupSearchCombobox";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useAside } from "shared/utils/aside";
import { to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

const CreateForm = () => {
    const nav = Route.useNavigate();
    const aside = useAside();

    const {
        data: { getAvailableEmails: availableEmails },
    } = useSuspenseQuery(sendersPageContactOptionsQueries.emails);
    const {
        data: { getAvailablePhoneNumbers: availablePhoneNumbers },
    } = useSuspenseQuery(sendersPageContactOptionsQueries.phoneNumbers);
    const createSender = useGraphqlMutation(muts.createSender);

    const form = useForm({
        mode: "uncontrolled",
        initialValues: {
            name: "",
            group: null as { id: string; name: string } | null | undefined,
            email: "",
            phoneNumber: "",
        },
        validate: {
            name: (it: string) => (it.length == 0 ? "Required" : null),
            group: (it: { id: string; name: string } | null | undefined) => (it == null ? "Required" : null),
            email: (it: string) => (it.length == 0 ? "Required" : null),
            phoneNumber: (it: string) => (it.length == 0 ? "Required" : null),
        },
    });

    const { group } = form.getValues();

    return (
        <form
            onSubmit={form.onSubmit((values, e) => {
                e?.preventDefault();
                createSender
                    .mutateAsync({
                        params: {
                            name: values.name,
                            group: values.group!.id,
                            fromEmail: values.email,
                            fromPhone: values.phoneNumber,
                        },
                    })
                    .then(async (res) => {
                        notify.show.success("Created sender");
                        await queryClient.invalidateQueries({ queryKey: ["senders", "table"] });
                        nav({ to: "/organization/senders/$senderId", params: { senderId: res.createSender.id } }).catch(notify.catch);
                    })
                    .catch(notify.catch);
            })}
            style={{ height: "100%" }}
        >
            <Stack justify="space-between" h="100%">
                <Stack>
                    <TextInput required label="Name" placeholder="Enter name..." key={form.key("name")} {...form.getInputProps("name")} />
                    <GroupSearchCombobox
                        required
                        fullWidth
                        selectedGroup={group}
                        setSelectedGroup={(g) => form.setFieldValue("group", g)}
                        nullable
                    />
                    <Select
                        required
                        label="Email"
                        allowDeselect={false}
                        data={Array.from(new Set(availableEmails.map(to("email"))))}
                        key={form.key("email")}
                        {...form.getInputProps("email")}
                    />
                    <Select
                        required
                        label="Phone Number"
                        allowDeselect={false}
                        data={Array.from(new Set(availablePhoneNumbers))}
                        key={form.key("phoneNumber")}
                        {...form.getInputProps("phoneNumber")}
                    />
                </Stack>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => aside.dismiss()}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!form.isValid()} loading={createSender.isPending}>
                        Create
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};

function Page() {
    const aside = useAside();
    const { href } = useLocation();
    const nav = Route.useNavigate();
    const matchRoute = useMatchRoute();

    useEffect(() => {
        if (!matchRoute({ to: "/organization/senders" })) aside.dismiss({ silent: true });
    }, [href]);

    useEffect(() => {
        aside.present({
            sticky: true,
            label: "Create Sender",
            component: <CreateForm />,
            didDismiss: () => nav({ to: "/organization/senders" }),
        });
    }, []);

    return null;
}

/** @public */
export const Route = createFileRoute("/_auth/organization/senders/create")({
    component: Page,
});

const muts = {
    createSender: graphql(`
        mutation SendersCreateSender($params: SenderInfoInput!) {
            createSender(params: $params) {
                id
            }
        }
    `),
};
